import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const PhotoPreviewContext = createContext(null);

/**
 * Hook to trigger the global photo preview lightbox.
 * Usage:
 *   const preview = usePhotoPreview();
 *   <img ... onClick={() => preview(src, alt)} className="... cursor-zoom-in" />
 */
export const usePhotoPreview = () => {
  const ctx = useContext(PhotoPreviewContext);
  // Soft-fail: if used outside the provider, return a no-op so render won't crash
  return ctx?.open || (() => {});
};

export const PhotoPreviewProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, src: '', alt: '' });

  const open = useCallback((src, alt = 'Player photo') => {
    if (!src) return;
    setState({ open: true, src, alt });
  }, []);

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  // ESC to close
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [state.open, close]);

  return (
    <PhotoPreviewContext.Provider value={{ open }}>
      {children}
      {state.open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Player photo preview"
          data-testid="photo-preview-modal"
          onClick={close}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-8 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); }}
            aria-label="Close preview"
            data-testid="photo-preview-close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-xl sm:text-2xl flex items-center justify-center transition-colors border border-white/20"
          >
            ✕
          </button>

          {/* Image */}
          <img
            src={state.src}
            alt={state.alt}
            data-testid="photo-preview-image"
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[85vh] sm:max-h-[90vh] object-contain rounded-lg shadow-2xl select-none"
            draggable={false}
          />
        </div>
      )}
    </PhotoPreviewContext.Provider>
  );
};

/**
 * Convenience component: a clickable thumbnail that opens the photo lightbox.
 * Falls back to a regular <img> if no src is provided.
 */
export const ClickablePhoto = React.forwardRef(function ClickablePhoto(
  { src, fullSrc, alt = 'Player photo', className = '', onClick, ...rest },
  ref
) {
  const preview = usePhotoPreview();
  if (!src) return null;
  return (
    <img
      ref={ref}
      src={src}
      alt={alt}
      onClick={(e) => {
        if (onClick) onClick(e);
        if (e.defaultPrevented) return;
        e.stopPropagation();
        preview(fullSrc || src, alt);
      }}
      className={`${className} cursor-zoom-in`}
      {...rest}
    />
  );
});

export default PhotoPreviewProvider;
